import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MessageSquare, Send, Phone, Mail, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Communications = () => {
  const [activeTab, setActiveTab] = useState('setup');
  const [whatsappMode, setWhatsappMode] = useState('web'); // 'web' or 'business'
  const [whatsappStatus, setWhatsappStatus] = useState({ connected: false });
  const [qrCode, setQrCode] = useState(null);
  const [businessApiStatus, setBusinessApiStatus] = useState({ configured: false, credits: 0 });
  const [isConnecting, setIsConnecting] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [statusPolling, setStatusPolling] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [messageContent, setMessageContent] = useState('');
  const [scheduledDate, setScheduledDate] = useState(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    content: '',
    category: ''
  });
  const [activeBulkJobs, setActiveBulkJobs] = useState([]);
  const [jobPolling, setJobPolling] = useState(null);
  
  // Image upload states for messaging
  const [composeImageFile, setComposeImageFile] = useState(null);
  const [composeImagePreview, setComposeImagePreview] = useState(null);
  
  useEffect(() => {
    fetchClients();
    fetchWhatsAppStatus();
    fetchMessages();
    fetchTemplates();
    fetchScheduledMessages();
    fetchBusinessApiStatus();
    fetchActiveBulkJobs();
    
    // Cleanup polling on unmount
    return () => {
      stopConnectionPolling();
      stopJobPolling();
    };
  }, []);

  // Start job polling when active jobs are detected
  useEffect(() => {
    const activeJobs = activeBulkJobs.filter(job => 
      job.status === 'queued' || job.status === 'running'
    );
    
    if (activeJobs.length > 0 && !jobPolling) {
      startJobPolling();
    } else if (activeJobs.length === 0 && jobPolling) {
      stopJobPolling();
    }
  }, [activeBulkJobs]);
  
  const fetchWhatsAppStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`);
      setWhatsappStatus(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
    }
  };
  
  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };
  
  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/message-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };
  
  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchScheduledMessages = async () => {
    try {
      const response = await axios.get(`${API}/scheduled-messages`);
      setScheduledMessages(response.data);
    } catch (error) {
      console.error('Error fetching scheduled messages:', error);
    }
  };

  const fetchBusinessApiStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/business-api/status`);
      setBusinessApiStatus(response.data);
    } catch (error) {
      console.error('Error fetching business API status:', error);
      setBusinessApiStatus({ configured: false, credits: 0 });
    }
  };
  
  const handleModeSwitch = async (mode) => {
    setWhatsappMode(mode);
    if (mode === 'web') {
      // Reset business API status and fetch QR if needed
      await fetchQrCode();
    } else {
      // Switch to business API mode
      setQrCode(null);
      await fetchBusinessApiStatus();
    }
  };

  const fetchQrCode = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/qr`);
      setQrCode(response.data.qr);
    } catch (error) {
      console.error('Error fetching QR code:', error);
    }
  };

  const connectWhatsAppWeb = async () => {
    setIsConnecting(true);
    try {
      console.log('Attempting to connect WhatsApp Web...');
      
      // First, initiate the connection
      const connectResponse = await axios.post(`${API}/whatsapp/connect`);
      console.log('WhatsApp connect response:', connectResponse.data);
      
      // Then fetch the QR code
      await fetchQrCode();
      
      // Update status
      await fetchWhatsAppStatus();
      
      // Show the QR modal
      setShowQrModal(true);
      
      // Start polling for connection status
      startConnectionPolling();
      
    } catch (error) {
      console.error('Error connecting WhatsApp Web:', error);
      // Still show modal so user can try refreshing
      setShowQrModal(true);
      startConnectionPolling(); // Start polling even on error
    } finally {
      setIsConnecting(false);
    }
  };

  const refreshQrCode = async () => {
    try {
      await fetchQrCode();
      await fetchWhatsAppStatus();
    } catch (error) {
      console.error('Error refreshing QR code:', error);
    }
  };

  // Start polling for connection status when QR modal is open
  const startConnectionPolling = () => {
    if (statusPolling) return; // Already polling
    
    const interval = setInterval(async () => {
      try {
        await fetchWhatsAppStatus();
        // If connected, stop polling and close modal after showing success
        if (whatsappStatus.connected) {
          stopConnectionPolling();
          console.log('✅ WhatsApp connected successfully!');
          
          // Show success message for 2 seconds, then close modal
          setTimeout(() => {
            setShowQrModal(false);
            setQrCode(null);
          }, 2000);
        }
      } catch (error) {
        console.error('Error polling WhatsApp status:', error);
      }
    }, 2000); // Poll every 2 seconds for more responsive detection
    
    setStatusPolling(interval);
  };

  const stopConnectionPolling = () => {
    if (statusPolling) {
      clearInterval(statusPolling);
      setStatusPolling(null);
    }
  };

  // Test connection function
  const testWhatsAppConnection = async () => {
    try {
      await fetchWhatsAppStatus();
      if (whatsappStatus.connected) {
        alert('✅ WhatsApp is connected and working!');
      } else {
        alert('❌ WhatsApp is not connected. Please scan the QR code first.');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      alert('❌ Error testing connection. Please try again.');
    }
  };

  // Refresh status function
  const refreshWhatsAppStatus = async () => {
    try {
      await fetchWhatsAppStatus();
      await fetchBusinessApiStatus();
      console.log('✅ Status refreshed successfully');
    } catch (error) {
      console.error('Error refreshing status:', error);
      alert('❌ Error refreshing status. Please try again.');
    }
  };

  // Disconnect function
  const disconnectWhatsApp = async () => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp?')) {
      return;
    }
    
    try {
      // Call disconnect endpoint if it exists, otherwise just refresh status
      await fetchWhatsAppStatus();
      alert('WhatsApp disconnected. You may need to unlink the device from your WhatsApp settings.');
    } catch (error) {
      console.error('Error disconnecting WhatsApp:', error);
      alert('Error disconnecting WhatsApp. Please try again.');
    }
  };

  const closeQrModal = () => {
    setShowQrModal(false);
    setQrCode(null);
    stopConnectionPolling(); // Stop polling when modal is closed
  };

  const configureBusinessApi = async (credentials) => {
    try {
      await axios.post(`${API}/whatsapp/business-api/configure`, credentials);
      await fetchBusinessApiStatus();
      alert('WhatsApp Business API configured successfully!');
    } catch (error) {
      console.error('Error configuring Business API:', error);
      alert('Error configuring Business API. Please check credentials.');
    }
  };

  const handleFilterClients = async (filter) => {
    setFilterType(filter);
    setSelectedClients([]);
    
    try {
      const response = await axios.post(`${API}/clients/filter`, {
        filter_type: filter,
        manual_client_ids: filter === 'manual_select' ? [] : null
      });
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Error filtering clients:', error);
    }
  };
  
  const handleClientSelection = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };
  
  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(client => client.id));
    }
  };
  
  const handleTemplateSelect = (templateId) => {
    if (templateId === 'none') {
      setSelectedTemplate('');
      setMessageContent('');
      return;
    }
    
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setMessageContent(template.content);
    } else {
      setSelectedTemplate('');
      setMessageContent('');
    }
  };
  
  const handleSendMessage = async () => {
    if (!messageContent.trim() || selectedClients.length === 0) {
      alert('Please select clients and enter a message');
      return;
    }
    
    try {
      // Regular message via API (image functionality temporarily disabled)
      const response = await axios.post(`${API}/messages/send`, {
        client_ids: selectedClients,
        template_id: selectedTemplate || null,
        content: messageContent,
        scheduled_at: scheduledDate ? scheduledDate.toISOString() : null
      });
      
      alert(response.data.message);
      
      // Clear form
      setMessageContent('');
      setSelectedTemplate('');
      setSelectedClients([]);
      setScheduledDate(null);
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message');
    }
  };

  // Image sending functionality temporarily removed to fix runtime errors
  
  const handleBulkSend = async () => {
    if (!messageContent && !messageText) {
      alert('Please enter a message');
      return;
    }
    
    try {
      const content = messageContent || messageText;
      const response = await axios.post(`${API}/messages/bulk-send`, {
        filter_type: filterType,
        manual_client_ids: filterType === 'manual_select' ? selectedClients : null,
        template_id: selectedTemplate || null,
        content: content,
        scheduled_at: scheduledDate ? scheduledDate.toISOString() : null
      });
      
      // Show job started confirmation
      const jobData = response.data;
      alert(`✅ Bulk messaging started!\n\n` +
            `📊 Sending to ${jobData.total_clients} clients\n` +
            `🕒 Estimated completion: ${new Date(jobData.estimated_completion).toLocaleString()}\n` +
            `📱 Messages will be sent with 30-40 second delays\n\n` +
            `You can navigate to other pages - progress will be tracked automatically!`);
      
      // Add job to active jobs list
      const newJob = {
        job_id: jobData.job_id,
        status: jobData.status,
        total_messages: jobData.total_clients,
        sent_count: 0,
        failed_count: 0,
        started_at: new Date().toISOString()
      };
      
      setActiveBulkJobs(prev => [newJob, ...prev]);
      
      // Start polling for status updates
      startJobPolling();
      
      // Clear form
      setMessageContent('');
      setMessageText('');
      setSelectedTemplate('');
      setScheduledDate(null);
      fetchMessages();
      
    } catch (error) {
      console.error('Error sending bulk message:', error);
      alert('Error sending bulk message: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/message-templates`, templateForm);
      fetchTemplates();
      setShowTemplateForm(false);
      setTemplateForm({ name: '', content: '', category: '' });
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  // Bulk messaging with status tracking
  const fetchActiveBulkJobs = async () => {
    try {
      const response = await axios.get(`${API}/messages/bulk-jobs`);
      setActiveBulkJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching bulk jobs:', error);
    }
  };

  const fetchJobStatus = async (jobId) => {
    try {
      const response = await axios.get(`${API}/messages/bulk-jobs/${jobId}/status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching job status:', error);
      return null;
    }
  };

  const startJobPolling = () => {
    if (jobPolling) return;

    const interval = setInterval(async () => {
      const activeJobs = activeBulkJobs.filter(job => 
        job.status === 'queued' || job.status === 'running'
      );

      if (activeJobs.length === 0) {
        stopJobPolling();
        return;
      }

      // Fetch updated status for all active jobs
      const updatedJobs = await Promise.all(
        activeJobs.map(async (job) => {
          const status = await fetchJobStatus(job.job_id);
          return status || job;
        })
      );

      // Update the jobs list
      setActiveBulkJobs(prevJobs => {
        const newJobs = [...prevJobs];
        updatedJobs.forEach(updatedJob => {
          const index = newJobs.findIndex(job => job.job_id === updatedJob.job_id);
          if (index !== -1) {
            newJobs[index] = updatedJob;
          }
        });
        return newJobs;
      });

    }, 5000); // Poll every 5 seconds

    setJobPolling(interval);
  };

  const stopJobPolling = () => {
    if (jobPolling) {
      clearInterval(jobPolling);
      setJobPolling(null);
    }
  };

  // Image handling functions for compose section
  const handleComposeImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      
      setComposeImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setComposeImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeComposeImage = () => {
    setComposeImageFile(null);
    setComposeImagePreview(null);
  };

  const sendMessageWithImage = async () => {
    if (!composeImageFile || selectedClients.length === 0) {
      alert('Please select clients and an image to send');
      return;
    }

    if (!messageContent.trim()) {
      alert('Please enter a message to send with the image');
      return;
    }

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result.split(',')[1]; // Remove data:image/...;base64, prefix
          
          // Send to each selected client
          for (const client of selectedClients) {
            const messageData = {
              phone_number: client.mobile,
              message: messageContent,
              image_data: base64Data,
              filename: `image_${client.name}_${composeImageFile.name}`
            };
            
            await axios.post(`${API}/whatsapp/web/send-image`, messageData);
          }
          
          alert(`Image message sent to ${selectedClients.length} clients successfully!`);
          
          // Clear form
          setMessageContent('');
          setSelectedClients([]);
          setComposeImageFile(null);
          setComposeImagePreview(null);
          fetchMessages();
          
        } catch (error) {
          console.error('Error sending image message:', error);
          alert('Failed to send image message. Please try again.');
        }
      };
      
      reader.readAsDataURL(composeImageFile);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Failed to process image. Please try again.');
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                WhatsApp Communications
              </h1>
              <p className="text-gray-600 text-lg">Send messages, manage templates, and stay connected with your clients</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <div className={`px-6 py-3 rounded-2xl shadow-lg flex items-center space-x-3 ${
                whatsappStatus.connected 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
                  : 'bg-gradient-to-r from-red-500 to-pink-600 text-white'
              }`}>
                <div className={`w-3 h-3 rounded-full ${whatsappStatus.connected ? 'bg-white' : 'bg-white'}`}></div>
                <div>
                  <div className="text-sm font-medium">WhatsApp Status</div>
                  <div className="text-lg font-bold">
                    {whatsappStatus.connected ? 'Connected' : 'Disconnected'}
                  </div>
                </div>
              </div>
              {!whatsappStatus.connected && (
                <Button 
                  onClick={connectWhatsAppWeb} 
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Connect WhatsApp
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-8">
        
        {/* Simple Job Progress Indicator - only show running jobs */}
        {activeBulkJobs.filter(job => job.status === 'running').length > 0 && (
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center space-x-4">
                <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800">Bulk Messages in Progress</h3>
                  <p className="text-sm text-gray-600">
                    {activeBulkJobs.filter(job => job.status === 'running').length} job(s) sending messages with 30-40 second delays
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-blue-600">
                    {activeBulkJobs.filter(job => job.status === 'running').reduce((acc, job) => acc + (job.sent_count || 0), 0)} sent
                  </div>
                  <div className="text-xs text-gray-500">
                    Navigate freely - jobs continue in background
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="setup">Setup</TabsTrigger>
            <TabsTrigger value="compose">Compose</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="jobs">Job Status</TabsTrigger>
          </TabsList>
          <div className="bg-white/80 backdrop-blur-sm border-0 rounded-2xl shadow-xl p-2 mb-8">
            <div className="grid grid-cols-5 gap-2">
              <Button
                variant={activeTab === 'setup' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('setup')}
                className={`py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                  activeTab === 'setup'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-orange-50'
                }`}
              >
                <Settings className="w-5 h-5 mr-2" />
                WhatsApp Setup
              </Button>
              <Button
                variant={activeTab === 'compose' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('compose')}
                className={`py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                  activeTab === 'compose'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-orange-50'
                }`}
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                Compose
              </Button>
              <Button
                variant={activeTab === 'templates' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('templates')}
                className={`py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                  activeTab === 'templates'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-orange-50'
                }`}
              >
                <FileText className="w-5 h-5 mr-2" />
                Templates
              </Button>
              <Button
                variant={activeTab === 'history' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('history')}
                className={`py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                  activeTab === 'history'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-orange-50'
                }`}
              >
                <Clock className="w-5 h-5 mr-2" />
                History
              </Button>
              <Button
                variant={activeTab === 'scheduled' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('scheduled')}
                className={`py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                  activeTab === 'scheduled'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-orange-50'
                }`}
              >
                <Clock className="w-5 h-5 mr-2" />
                Scheduled
              </Button>
            </div>
          </div>
        
        {/* Setup Tab */}
        <TabsContent value="setup" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* WhatsApp Web Setup */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-bold text-gray-800 flex items-center">
                      <MessageCircle className="w-6 h-6 mr-2 text-green-600" />
                      WhatsApp Web
                    </CardTitle>
                    <CardDescription className="text-gray-600">
                      Connect using WhatsApp Web for personal accounts
                    </CardDescription>
                  </div>
                  <Badge variant={whatsappMode === 'web' ? 'default' : 'outline'}>
                    {whatsappMode === 'web' ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">✅ Features:</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Free to use</li>
                    <li>• Quick setup with QR code</li>
                    <li>• Send messages to individuals</li>
                    <li>• Basic message templates</li>
                  </ul>
                </div>
                {whatsappMode === 'web' && (
                  <>
                    {whatsappStatus.connected ? (
                      <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                        <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                        <p className="text-green-800 font-medium">WhatsApp Connected!</p>
                        <p className="text-green-600 text-sm">Ready to send messages</p>
                      </div>
                    ) : (
                      <Button 
                        onClick={connectWhatsAppWeb}
                        disabled={isConnecting}
                        className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                      >
                        {isConnecting ? 'Connecting...' : 'Connect WhatsApp Web'}
                      </Button>
                    )}
                  </>
                )}
                
                {whatsappMode !== 'web' && (
                  <Button 
                    onClick={() => handleModeSwitch('web')}
                    className="w-full bg-gray-600 hover:bg-gray-700"
                  >
                    Switch to WhatsApp Web
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* WhatsApp Business API Setup */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-bold text-gray-800 flex items-center">
                      <Shield className="w-6 h-6 mr-2 text-blue-600" />
                      WhatsApp Business API
                    </CardTitle>
                    <CardDescription className="text-gray-600">
                      Professional messaging with advanced features
                    </CardDescription>
                  </div>
                  <Badge variant={whatsappMode === 'business' ? 'default' : 'outline'}>
                    {whatsappMode === 'business' ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">🚀 Premium Features:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Bulk messaging capabilities</li>
                    <li>• Message templates with approval</li>
                    <li>• Scheduled messaging</li>
                    <li>• Analytics and reporting</li>
                    <li>• Higher message limits</li>
                  </ul>
                </div>
                {businessApiStatus.configured ? (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <span className="text-green-800 font-medium">API Configured</span>
                      <span className="text-green-600 text-sm">Credits: {businessApiStatus.credits}</span>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <span className="text-orange-800 font-medium">API Not Configured</span>
                  </div>
                )}
                <div className="space-y-2">
                  <Button 
                    onClick={() => handleModeSwitch('business')}
                    className={`w-full ${whatsappMode === 'business' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-700'}`}
                    disabled={whatsappMode === 'business'}
                  >
                    {whatsappMode === 'business' ? 'Currently Active' : 'Switch to Business API'}
                  </Button>
                  
                  {!businessApiStatus.configured && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full">
                          <Settings className="w-4 h-4 mr-2" />
                          Configure API
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Configure WhatsApp Business API</DialogTitle>
                          <DialogDescription>
                            Enter your WhatsApp Business API credentials. Contact Super Admin if you need assistance.
                          </DialogDescription>
                        </DialogHeader>
                        <BusinessApiConfigForm onSubmit={configureBusinessApi} />
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  <Button variant="outline" className="w-full text-blue-600" asChild>
                    <a href="/WHATSAPP_SETUP_INSTRUCTIONS.md" target="_blank">
                      <FileText className="w-4 h-4 mr-2" />
                      Setup Instructions
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Configuration Section */}
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-800 flex items-center">
                <Settings className="w-6 h-6 mr-2 text-gray-600" />
                Configuration Settings
              </CardTitle>
              <CardDescription className="text-gray-600">
                Manage your WhatsApp integration settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-800">Connection Status</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">WhatsApp Connection</span>
                      <Badge variant={whatsappStatus.connected ? 'default' : 'destructive'}>
                        {whatsappStatus.connected ? 'Connected' : 'Disconnected'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Current Mode</span>
                      <Badge variant="outline">
                        {whatsappMode === 'web' ? 'WhatsApp Web' : 'Business API'}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-800">Quick Actions</h4>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={testWhatsAppConnection}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Test Connection
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={refreshWhatsAppStatus}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Refresh Status
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-red-600 hover:text-red-700"
                      onClick={disconnectWhatsApp}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Compose Messages Tab */}
        <TabsContent value="compose" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Select Clients
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={filterType === 'all' ? 'default' : 'outline'}
                    onClick={() => handleFilterClients('all')}
                  >
                    All Clients
                  </Button>
                  <Button
                    size="sm"
                    variant={filterType === 'fee_due' ? 'default' : 'outline'}
                    onClick={() => handleFilterClients('fee_due')}
                  >
                    <IndianRupee className="w-4 h-4 mr-1" />
                    Fee Due
                  </Button>
                  <Button
                    size="sm"
                    variant={filterType === 'gst_enabled' ? 'default' : 'outline'}
                    onClick={() => handleFilterClients('gst_enabled')}
                  >
                    GST Enabled
                  </Button>
                  <Button
                    size="sm"
                    variant={filterType === 'recent_clients' ? 'default' : 'outline'}
                    onClick={() => handleFilterClients('recent_clients')}
                  >
                    Recent
                  </Button>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {filteredClients.length} clients found
                  </span>
                  <Button size="sm" variant="outline" onClick={handleSelectAll}>
                    <CheckSquare className="w-4 h-4 mr-1" />
                    {selectedClients.length === filteredClients.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {filteredClients.map((client) => (
                    <div
                      key={client.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedClients.includes(client.id)
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => handleClientSelection(client.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">{client.name}</div>
                          <div className="text-sm text-gray-600">{client.organization_name}</div>
                          <div className="text-xs text-gray-500">{client.mobile}</div>
                        </div>
                        {selectedClients.includes(client.id) && (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Message Composition */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Compose Message
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Message Template</Label>
                  <Select value={selectedTemplate || undefined} onValueChange={handleTemplateSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select template (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No template</SelectItem>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Message Content</Label>
                  <textarea
                    className="w-full min-h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Type your message here... You can use variables like {client_name}, {organization_name}"
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Available variables: {'{client_name}'}, {'{organization_name}'}
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Upload className="w-4 h-4 mr-2 text-purple-600" />
                      <span className="text-sm text-purple-600 font-medium">
                        Image Attachment
                      </span>
                    </div>
                    {composeImagePreview && (
                      <button
                        onClick={removeComposeImage}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {composeImagePreview ? (
                    <div className="space-y-3">
                      <img 
                        src={composeImagePreview} 
                        alt="Selected" 
                        className="max-w-full max-h-32 rounded border"
                      />
                      <div className="text-xs text-gray-600">
                        {composeImageFile?.name} ({(composeImageFile?.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-purple-300 rounded p-4 text-center">
                      <Upload className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                      <p className="text-sm text-purple-600 mb-2">Select an image to send with your message</p>
                      <button
                        onClick={() => document.getElementById('compose-image-input').click()}
                        className="text-xs text-purple-700 hover:underline"
                      >
                        Choose Image
                      </button>
                      <p className="text-xs text-purple-500 mt-1">Max 5MB • JPG, PNG, GIF</p>
                    </div>
                  )}
                  
                  <input
                    id="compose-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleComposeImageSelect}
                    className="hidden"
                  />
                  
                  {composeImageFile && (
                    <div className="mt-3 pt-3 border-t border-purple-200">
                      <button
                        onClick={sendMessageWithImage}
                        className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700 text-sm font-medium"
                        disabled={selectedClients.length === 0 || !messageContent.trim()}
                      >
                        Send Image Message to {selectedClients.length} Client{selectedClients.length !== 1 ? 's' : ''}
                      </button>
                    </div>
                  )}
                </div>
                
                <div>
                  <Label>Schedule Message (Optional)</Label>
                  <DatePicker
                    date={scheduledDate}
                    onDateChange={setScheduledDate}
                    placeholder="Send immediately"
                  />
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={handleSendMessage}
                    disabled={!whatsappStatus.connected || selectedClients.length === 0 || !messageContent.trim()}
                    className="flex-1"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send to Selected ({selectedClients.length})
                  </Button>
                  <Button
                    onClick={handleBulkSend}
                    disabled={!whatsappStatus.connected || !messageContent.trim()}
                    variant="outline"
                    className="flex-1"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Bulk Send ({filteredClients.length})
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Message History</CardTitle>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <p className="text-gray-500">No messages sent yet</p>
              ) : (
                <div className="space-y-2">
                  {messages.map((message, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{message.phone_number}</p>
                          <p className="text-sm text-gray-600">{message.content}</p>
                        </div>
                        <Badge variant={message.status === 'sent' ? 'default' : 'secondary'}>
                          {message.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Job Status Tab */}
        <TabsContent value="jobs" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
                Bulk Messaging Jobs
              </CardTitle>
              <CardDescription>
                Track your bulk messaging campaigns with detailed status and progress
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeBulkJobs.length === 0 ? (
                <div className="text-center py-8">
                  <Send className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No bulk messaging jobs found</p>
                  <p className="text-sm text-gray-400 mt-1">Start a bulk message in the Compose tab</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activeBulkJobs.map((job) => (
                    <div key={job.job_id} className="p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded-full ${
                            job.status === 'running' ? 'bg-blue-500 animate-pulse' :
                            job.status === 'completed' ? 'bg-green-500' :
                            job.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                          }`}></div>
                          <span className="font-semibold text-gray-800">
                            {job.status === 'running' ? 'Sending Messages' :
                             job.status === 'completed' ? 'Completed' :
                             job.status === 'failed' ? 'Failed' : 'Queued'}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          Job ID: {job.job_id.slice(0, 8)}
                        </Badge>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span>Progress: {job.sent_count || 0} / {job.total_messages} sent</span>
                          <span>{Math.round(((job.sent_count || 0) / job.total_messages) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all duration-300 ${
                              job.status === 'completed' ? 'bg-green-500' :
                              job.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${((job.sent_count || 0) / job.total_messages) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Current Status:</span>
                          <p className="font-medium">
                            {job.current_client ? `Sending to: ${job.current_client}` : 
                             job.status === 'completed' ? 'All messages sent' :
                             job.status === 'failed' ? 'Job failed' : 'Preparing...'}
                          </p>
                        </div>
                        {job.failed_count > 0 && (
                          <div>
                            <span className="text-gray-500">Failed Messages:</span>
                            <p className="font-medium text-red-600">{job.failed_count}</p>
                          </div>
                        )}
                        {job.started_at && (
                          <div>
                            <span className="text-gray-500">Started:</span>
                            <p className="font-medium">{new Date(job.started_at).toLocaleString()}</p>
                          </div>
                        )}
                        {job.estimated_completion && job.status === 'running' && (
                          <div>
                            <span className="text-gray-500">Estimated Completion:</span>
                            <p className="font-medium text-blue-600">{new Date(job.estimated_completion).toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Message Templates</h3>
            <Dialog open={showTemplateForm} onOpenChange={setShowTemplateForm}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Message Template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTemplate} className="space-y-4">
                  <div>
                    <Label>Template Name</Label>
                    <Input
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                      placeholder="e.g., Payment Reminder"
                      required
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Input
                      value={templateForm.category}
                      onChange={(e) => setTemplateForm({...templateForm, category: e.target.value})}
                      placeholder="e.g., Reminders, Greetings"
                    />
                  </div>
                  <div>
                    <Label>Message Content</Label>
                    <textarea
                      className="w-full min-h-24 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={templateForm.content}
                      onChange={(e) => setTemplateForm({...templateForm, content: e.target.value})}
                      placeholder="Dear {client_name}, this is a reminder..."
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setShowTemplateForm(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">Create Template</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      {template.category && (
                        <CardDescription>{template.category}</CardDescription>
                      )}
                    </div>
                    <div className="flex space-x-1">
                      <Button size="sm" variant="ghost">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {template.content}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3 w-full"
                    onClick={() => {
                      setActiveTab('compose');
                      handleTemplateSelect(template.id);
                    }}
                  >
                    Use Template
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
        
        {/* Message History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Message History</CardTitle>
              <CardDescription>All sent and scheduled messages</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead>Template</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.map((message) => (
                    <TableRow key={message.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{message.client?.name}</div>
                          <div className="text-sm text-gray-500">{message.phone_number}</div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate">{message.content}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          message.status === 'sent' ? 'default' :
                          message.status === 'delivered' ? 'success' :
                          message.status === 'failed' ? 'destructive' :
                          message.status === 'scheduled' ? 'secondary' : 'outline'
                        }>
                          {message.status.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {message.sent_at ? new Date(message.sent_at).toLocaleString() : 
                         message.scheduled_at ? `Scheduled: ${new Date(message.scheduled_at).toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell>
                        {message.template?.[0]?.name || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Scheduled Messages Tab */}
        <TabsContent value="scheduled" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Scheduled Messages
              </CardTitle>
              <CardDescription>Messages scheduled for future delivery</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Scheduled For</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.filter(msg => msg.status === 'scheduled').map((message) => (
                    <TableRow key={message.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{message.client?.name}</div>
                          <div className="text-sm text-gray-500">{message.phone_number}</div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate">{message.content}</div>
                      </TableCell>
                      <TableCell>
                        {new Date(message.scheduled_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">SCHEDULED</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>

      {/* QR Code Modal */}
      <QrCodeModal 
        isOpen={showQrModal} 
        onClose={closeQrModal} 
        qrCode={qrCode}
        onRefresh={refreshQrCode}
        isConnected={whatsappStatus.connected}
      />
    </div>
  );
};

export default Communications;
